import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Title, Text, Button } from '@tremor/react';
import { BookOpenIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { getBeSources, saveBeSources } from '../../Requests/BehavioralEconomicsRequests';
import { sanitizeUserInput } from '../../utils/security';
import UpgradedLoader from '../UpgradedLoader';

/**
 * "Base" tab: browse the curated Behavioral Economics site list, tick the ones relevant to
 * the study, and add your own links/reports. Saved per project.
 */
function BeSourcesTab({ projectUuid, analysisUuid }) {
    const { t } = useTranslation();
    const [curated, setCurated] = useState([]);
    const [selected, setSelected] = useState([]); // urls
    const [customLinks, setCustomLinks] = useState([]);
    const [newLink, setNewLink] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savedMsg, setSavedMsg] = useState(false);

    useEffect(() => {
        getBeSources(projectUuid, analysisUuid)
            .then((res) => {
                setCurated(res.data.curated_sources ?? []);
                setSelected(res.data.selection?.selected_site_urls ?? []);
                setCustomLinks(res.data.selection?.custom_links ?? []);
            })
            .finally(() => setLoading(false));
    }, [projectUuid, analysisUuid]);

    const toggle = (url) => {
        setSelected((prev) => (prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]));
    };

    const addLink = () => {
        const clean = sanitizeUserInput(newLink.trim());
        if (clean && !customLinks.includes(clean)) {
            setCustomLinks((prev) => [...prev, clean]);
            setNewLink('');
        }
    };

    const save = () => {
        setSaving(true);
        setSavedMsg(false);
        saveBeSources(projectUuid, analysisUuid, { selected_site_urls: selected, custom_links: customLinks })
            .then(() => {
                setSavedMsg(true);
                setTimeout(() => setSavedMsg(false), 3000);
            })
            .finally(() => setSaving(false));
    };

    if (loading) return <UpgradedLoader />;

    return (
        <div data-testid="be-sources-tab">
            <div className="flex items-center space-x-3 mb-2">
                <BookOpenIcon className="h-6 w-6 text-iavisus-100" />
                <Title className="text-lg font-semibold">{t('BehavioralEconomics.Sources.Title')}</Title>
            </div>
            <Text className="mb-4">{t('BehavioralEconomics.Sources.Description')}</Text>

            {/* Custom links */}
            <div className="mb-6">
                <Text className="font-bold mb-2">{t('BehavioralEconomics.Sources.YourLinks')}</Text>
                <div className="flex gap-2 mb-2">
                    <input
                        type="url"
                        value={newLink}
                        onChange={(e) => setNewLink(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addLink()}
                        placeholder={t('BehavioralEconomics.Sources.CustomLinkPlaceholder')}
                        className="flex-grow border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                    <button
                        type="button"
                        onClick={addLink}
                        className="flex items-center gap-1 border border-iavisus-100 text-iavisus-100 rounded-md px-3 py-2 text-xs font-bold"
                    >
                        <PlusIcon className="w-4 h-4" /> {t('BehavioralEconomics.Sources.AddLink')}
                    </button>
                </div>
                {customLinks.map((link) => (
                    <div key={link} className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2 mb-1 text-sm">
                        <span className="truncate">{link}</span>
                        <button type="button" onClick={() => setCustomLinks((p) => p.filter((l) => l !== link))}>
                            <TrashIcon className="w-4 h-4 text-gray-400 hover:text-red-500" />
                        </button>
                    </div>
                ))}
            </div>

            {/* Curated base */}
            <Text className="font-bold mb-2">{t('BehavioralEconomics.Sources.CuratedBase')}</Text>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6">
                {curated.map((s) => (
                    <label key={s.url} className="flex items-start gap-2 border border-gray-200 rounded-md px-3 py-2 cursor-pointer hover:bg-gray-50">
                        <input type="checkbox" className="mt-1" checked={selected.includes(s.url)} onChange={() => toggle(s.url)} />
                        <span className="text-sm">
                            <span className="font-medium block">{s.name}</span>
                            <a href={s.url} target="_blank" rel="noreferrer" className="text-iavisus-100 text-xs break-all" onClick={(e) => e.stopPropagation()}>
                                {s.url}
                            </a>
                        </span>
                    </label>
                ))}
            </div>

            <div className="flex items-center gap-3">
                <Button onClick={save} loading={saving} className="bg-iavisus-900">
                    {t('BehavioralEconomics.Sources.Save')}
                </Button>
                {savedMsg && <Text className="text-green-600">{t('BehavioralEconomics.Sources.Saved')}</Text>}
            </div>
        </div>
    );
}

export default BeSourcesTab;
